import React from "react";
import { motion } from "framer-motion";
import { FileCheck, HardHat, Users, Target } from "lucide-react";

/* =====================================================================
   DEALER REQUIREMENTS — Who Can Become a LIFTILE Dealer?
   Four premium rounded cards on cream background. Soft shadow,
   orange hover lift + border, equal-height grid, staggered fade-up
   entrance — matches the visual rhythm of the "Why Partner With
   LIFTILE" cards above for site-wide consistency.
   ===================================================================== */

const REQUIREMENTS = [
  {
    icon: FileCheck,
    title: "Business Registration",
    desc: "A registered business with valid legal documentation.",
  },
  {
    icon: HardHat,
    title: "Construction Equipment Experience",
    desc: "Experience in construction equipment or industrial products is preferred but not mandatory.",
  },
  {
    icon: Users,
    title: "Customer-First Approach",
    desc: "Commitment to providing excellent customer service and long-term relationships.",
  },
  {
    icon: Target,
    title: "Growth Mindset",
    desc: "A passion for growing with a trusted manufacturing partner and expanding the LIFTILE network.",
  },
];

export default function DealerRequirements() {
  return (
    <section
      data-testid="dealer-requirements"
      className="bg-liftile-cream-light py-24 md:py-32 text-liftile-matte-black"
    >
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <div className="heading-eyebrow mb-3">Dealer Requirements</div>
          <h2
            data-testid="dealer-requirements-heading"
            className="display-2"
          >
            Who Can Become a{" "}
            <span className="text-liftile-orange">LIFTILE Dealer</span>?
          </h2>
          <p className="font-body text-base md:text-lg text-liftile-mid-gray/80 mt-5 leading-relaxed">
            We believe strong partnerships are built on professionalism,
            customer commitment and long-term business growth.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-14 md:mt-16">
          {REQUIREMENTS.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.title}
                data-testid={`dealer-requirement-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.55,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group relative h-full flex flex-col bg-white rounded-2xl p-8 md:p-9 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_40px_rgba(255,107,26,0.18)] border border-transparent hover:border-liftile-orange/40 hover:-translate-y-1 transition-all duration-500"
              >
                {/* Top-right corner accent line on hover */}
                <span className="absolute top-0 right-0 h-[2px] w-0 bg-liftile-orange transition-all duration-500 group-hover:w-16 rounded-bl-full" />

                <div className="w-14 h-14 rounded-xl bg-liftile-orange/10 group-hover:bg-liftile-orange flex items-center justify-center transition-colors duration-500">
                  <Icon
                    className="w-6 h-6 text-liftile-orange group-hover:text-white transition-colors duration-500"
                    strokeWidth={1.6}
                  />
                </div>

                <h3 className="font-heading text-base md:text-[17px] uppercase tracking-wider font-semibold mt-6 text-liftile-matte-black leading-snug">
                  {r.title}
                </h3>

                <p className="font-body text-sm md:text-[15px] text-liftile-mid-gray/80 mt-3 leading-relaxed flex-1">
                  {r.desc}
                </p>

                <span className="block w-10 h-[2px] bg-liftile-orange/30 mt-7 group-hover:w-20 group-hover:bg-liftile-orange transition-all duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
